import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { CookieService } from '../utils/cookies/cookies.service';
import { Config } from '../config/config.service';
import { TranslationService } from '../translation/translation.service';
import { connectToPrivateChannel } from '../../NGRX/coreWebsockets/coreWebsockets.actions';
import {
  AppState,
  callMicrositeSessions,
  selectMicrositeSessions,
  selectMicrositeSessionsIsLoggedIn,
} from '../../NGRX/core.state';
import {
  AuthTokenState,
  MicrositeSessionsState,
} from '../../NGRX/coreAuth/coreAuth.models';
import { CB_CORE_ENV } from '../../environment/cb-core-env.token';
import * as _ from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class IdentityService {
  private store = inject(Store<AppState>);
  private router = inject(Router);
  private cookieService = inject(CookieService);
  private config = inject(Config);
  private translationService = inject(TranslationService);

  private env = inject(CB_CORE_ENV);
  // Blocks a late in-flight response from resurrecting a token just explicitly cleared —
  // bounded to a short window, not permanent, so a legitimate new session reusing the same
  // token value isn't locked out indefinitely
  private readonly EXPIRED_TOKEN_TTL_MS = 15000;
  private expiredTokens = signal<Array<{ token: string; expiresAt: number }>>(
    [],
  );
  private sessionTimeout: ReturnType<typeof setTimeout> | undefined;
  // At most one "go to root after login" waiting at a time, however many requests expire
  private pendingExpireRedirect: Subscription | undefined;
  // Language headers are absent on the very first request, then set after the first response —
  // matching old setAcceptLanguageHeader() behaviour in the map operator
  private currentLanguageHeaders: Record<string, string> = {};

  private micrositeSessionsState: Signal<MicrositeSessionsState> =
    this.store.selectSignal(selectMicrositeSessions);
  private authToken: Signal<AuthTokenState> = computed(
    () => this.micrositeSessionsState()?.AuthToken,
  );

  readonly defaultHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: 'application/json',
  };

  async getStoredHeaders(): Promise<Record<string, string>> {
    const storedHeaders: Record<string, string> = {};
    const cookieHeaders = await this.cookieService.get('cb_auth_headers');
    const accessToken = this.authToken()?.tokens?.access_token;

    if (accessToken) {
      storedHeaders['Authorization'] = accessToken;
    }

    if (cookieHeaders) {
      if (cookieHeaders['access-token'])
        storedHeaders['access-token'] = cookieHeaders['access-token'];
      if (cookieHeaders['client'])
        storedHeaders['client'] = cookieHeaders['client'];
      if (cookieHeaders['uid']) storedHeaders['uid'] = cookieHeaders['uid'];
      if (cookieHeaders['expiry'])
        storedHeaders['expiry'] = cookieHeaders['expiry'];
    }

    // Language headers are absent on first request; populated after first response
    return { ...storedHeaders, ...this.currentLanguageHeaders };
  }

  async updateIdentityFromResponse(headers: any, url: string): Promise<void> {
    const accessToken = headers.get('access-token');
    const client = headers.get('client');
    const uid = headers.get('uid');
    const expiry = headers.get('expiry');

    if (accessToken && this.isRecentlyExpired(accessToken)) {
      await this.clearIdentity();
      return;
    }

    // Update language headers after each response, same as old setAcceptLanguageHeader()
    const lang = this.translationService.getAcceptLanguageHeader();
    this.currentLanguageHeaders = {
      'Accept-Language': lang,
      'X-CB-Language': lang,
    };

    if (accessToken && client && uid) {
      if (!expiry) {
        console.warn(
          '[IdentityService] Devise-Token-Auth response is missing the expiry header — session-expiry redirect will not be scheduled',
          { url },
        );
      }

      const newIdentity = { 'access-token': accessToken, client, uid, expiry };
      await this.cookieService.set('cb_auth_headers', newIdentity);

      if (url.includes('sessions')) {
        // Mirror old behaviour: dispatch defaultHeaders + Devise Token headers + current language
        this.store.dispatch(
          connectToPrivateChannel({
            headers: _.cloneDeep({
              ...this.defaultHeaders,
              ...newIdentity,
              ...this.currentLanguageHeaders,
            }),
          }),
        );
      }
    } else if (accessToken || client || uid) {
      // Backend sent a partial Devise-Token-Auth identity — access-token/client/uid must
      // arrive together or not at all. Silently dropping this (prior behavior) makes a
      // broken token-auth login look identical to a normal, identity-less response.
      console.warn(
        '[IdentityService] Partial Devise-Token-Auth headers on response — identity NOT persisted (access-token/client/uid must all be present together)',
        {
          url,
          hasAccessToken: !!accessToken,
          hasClient: !!client,
          hasUid: !!uid,
          hasExpiry: !!expiry,
        },
      );
    }

    this.handleExpiryTimer(expiry);
  }

  handleExpiryTimer(expiry: any): void {
    if (!expiry) return;
    const diff =
      new Date(expiry * 1000).getTime() - new Date().getTime() - 30000;

    if (diff > 0) {
      clearTimeout(this.sessionTimeout);
      this.sessionTimeout = setTimeout(() => {
        this.clearIdentity().then(() => this.redirectOnExpire());
      }, diff);
    }
  }

  async clearIdentity(): Promise<void> {
    const current = await this.cookieService.get('cb_auth_headers');
    if (current?.['access-token']) {
      const expiresAt = Date.now() + this.EXPIRED_TOKEN_TTL_MS;
      this.expiredTokens.update((tokens) => [
        ...this.pruneExpiredTokens(tokens),
        { token: current['access-token'], expiresAt },
      ]);
    }
    await this.cookieService.remove('cb_auth_headers');
    clearTimeout(this.sessionTimeout);
  }

  private isRecentlyExpired(token: string): boolean {
    const pruned = this.pruneExpiredTokens(this.expiredTokens());
    this.expiredTokens.set(pruned);
    return pruned.some((entry) => entry.token === token);
  }

  private pruneExpiredTokens(
    tokens: Array<{ token: string; expiresAt: number }>,
  ): Array<{ token: string; expiresAt: number }> {
    const now = Date.now();
    return tokens.filter((entry) => entry.expiresAt > now);
  }

  redirectOnExpire(): void {
    const rootUrl = this.config.CONFIG.rootUrl;

    if (!this.env.core?.customSessionExpireRedirect) {
      this.store.dispatch(callMicrositeSessions());
      this.router.navigateByUrl(rootUrl).catch(console.error);
    } else {
      // Only meaningful for the page that expired. If the user is somewhere else by the time they
      // log in (a deep link opened its own page after a cold-start 401, say), that page is fresh
      // and pulling it back to root would throw the deep link away.
      const expiredAtUrl = this.router.url;
      this.pendingExpireRedirect?.unsubscribe();
      this.pendingExpireRedirect = this.store
        .select(selectMicrositeSessionsIsLoggedIn)
        .pipe(filter(Boolean), take(1))
        .subscribe(() => {
          if (this.router.url !== expiredAtUrl) return;
          this.store.dispatch(callMicrositeSessions());
          this.router.navigateByUrl(rootUrl).catch(console.error);
        });
    }
  }

  errorHandler(err: any): {
    status: number;
    code: string;
    message: string | null;
    error_code?: string;
    errors: any;
    info: any;
  } {
    try {
      return {
        status: err?.error?.status || -1,
        code:
          err?.error?.error || _.result(err, 'error.errors[0].code', 'DEFAULT'),
        error_code: err?.error?.error_code,
        message: _.result(err, 'error.errors[0].message', null),
        errors: err?.error?.errors,
        info: err?.error?.info || {},
      };
    } catch (e) {
      return {
        status: -1,
        code: 'DEFAULT',
        message: null,
        errors: undefined,
        info: {},
      };
    }
  }
}
